import type { CoachDiscoveryForm } from "~/lib/schemas/coach-discovery";
import { server$ } from "@builder.io/qwik-city";
import { getDB } from "./db";
import {
  aliasedTable,
  and,
  desc,
  eq,
  inArray,
  sql,
  isNotNull as isNotNullSql,
  SQL,
  isNotNull,
  or,
  like,
  arrayContains,
  arrayOverlaps,
} from "drizzle-orm";
import { calculateFormatMatchScore } from "./coach-helper";
import {
  coachDetails,
  coaches,
  profiles,
  coachEmbeddings,
  CoachEmbeddingType,
  coachExpertise,
  expertiseSubcategories,
  expertiseCategories,
  ExpertiseType,
} from "../../drizzle/schema";

import {
  CoachPersonalityOptions,
  PERSONALITY_CATEGORIES,
  PersonalityCategory,
  personalityMapping,
} from "./personality-mapping";

function preferredPersonalityCategories(
  preferredPersonalities: CoachPersonalityOptions[],
) {
  // Use reduce for a more functional approach
  return PERSONALITY_CATEGORIES.reduce(
    (result, category) => {
      // Use Set for automatic deduplication
      const uniqueValues = new Set(
        preferredPersonalities.flatMap(
          (personality) => personalityMapping[personality][category],
        ),
      );

      return {
        ...result,
        [category]: Array.from(uniqueValues),
      };
    },
    {} as Record<PersonalityCategory, string[]>,
  );
}

function personalityCategoryColumn(category: PersonalityCategory) {
  switch (category) {
    case "disc":
      return coachDetails.discType;
    case "mbti":
      return coachDetails.mbtiType;
    case "enneagram":
      return coachDetails.enneagramType;
    case "humanDesign":
      return coachDetails.humanDesignType;
  }
}

function generatePersonalityMatchSQL(
  personalityOptions: CoachPersonalityOptions[],
): SQL {
  const preferenceCategories =
    preferredPersonalityCategories(personalityOptions);

  const conditions = PERSONALITY_CATEGORIES.map((category) => {
    const column = personalityCategoryColumn(category);
    const values = preferenceCategories[category];

    return sql`
      (CASE 
        WHEN ${isNotNull(column)} AND ${inArray(column, values)}
        THEN 1 ELSE 0 
      END)`;
  });

  return sql.join(conditions, sql` + `);
}

export type CoachMatchPreferences = CoachDiscoveryForm & {
  embedding?: number[];
};

export const getCoachMatches = server$(async function (
  preferences: CoachMatchPreferences,
  limit: number = 10,
  queryLimit: number = limit * 10,
  options?: {
    /** Weights adjustment for different matching components (0-1) */
    vectorSimilarityWeight?: number;

    specialtyWeight?: number;
    locationWeight?: number;
    faithWeight?: number;
    genderWeight?: number;
    ageWeight?: number;
    sessionFormatWeight?: number;
    experienceLevelWeight?: number;
    coachingApproachWeight?: number;
    personalityWeight?: number;
    /** Whether to apply hard filtering for must-have preferences */
    enforceHardRequirements?: boolean;
  },
) {
  const db = getDB();

  // Default options (similar to current implementation)
  const matchingOptions = {
    vectorSimilarityWeight: options?.vectorSimilarityWeight ?? 3.0,

    specialtyWeight: options?.specialtyWeight ?? 10.0,
    // Preferred format: 1:1/group // hard
    sessionFormatWeight: options?.sessionFormatWeight ?? 0.1, // hard
    // Language // hard
    locationWeight: options?.locationWeight ?? 2.0, // hard
    genderWeight: options?.genderWeight ?? 0.1, // hard
    ageWeight: options?.ageWeight ?? 1.0,

    experienceLevelWeight: options?.experienceLevelWeight ?? 3.5,
    coachingApproachWeight: options?.coachingApproachWeight ?? 1.0,
    personalityWeight: options?.personalityWeight ?? 1.0,
    faithWeight: options?.faithWeight ?? 0.1, // hard

    enforceHardRequirements: options?.enforceHardRequirements ?? true,
  };

  console.log("=== Finding matches ===\nPreferences\n\n");
  console.log(preferences);
  console.log("\n\nOptions:\n\n");
  console.log(matchingOptions);
  console.log("\n\n");

  // Hard filters logic
  const hardFilters = [
    isNotNullSql(coaches.profilePictureUrl),
    eq(coaches.profile_manually_edited, true),
  ];

  if (matchingOptions.enforceHardRequirements) {
    if (
      preferences.prefersCoachLocation === "Yes" &&
      preferences.coachLocationCountry
    ) {
      hardFilters.push(
        eq(profiles.primaryCountry, preferences.coachLocationCountry),
      );
    }

    if (preferences.preferredLanguages.length > 0) {
      const languageValues = preferences.preferredLanguages;

      const condition = or(
        ...languageValues.map(
          (lang): SQL => like(profiles.languages, `%${lang}%`),
        ),
      );

      if (condition) {
        hardFilters.push(
          and(isNotNullSql(profiles.languages), condition) as SQL,
        );
      }
    }

    if (
      preferences.sessionDeliveryMethods &&
      preferences.sessionDeliveryMethods !== "No preference"
    ) {
      hardFilters.push(
        arrayOverlaps(coachDetails.sessionDeliveryMethods, [
          preferences.sessionDeliveryMethods,
        ]),
      );
    }

    if (
      (preferences.coachingFormats?.length ?? 0) > 0 &&
      !["any", "both", "undecided"].includes(preferences.coachingFormats)
    ) {
      hardFilters.push(
        arrayOverlaps(coachDetails.coachingFormats, [
          preferences.coachingFormats,
        ]),
      );
    }

    if (
      preferences.faithIntegration &&
      Array.isArray(preferences.faithIntegration) &&
      preferences.faithIntegration.length > 0 &&
      !preferences.faithIntegration.includes("No preference")
    ) {
      const faithValues = preferences.faithIntegration
        .map((faith) => `'${faith.replace(/'/g, "''")}' `)
        .join(",");

      hardFilters.push(
        sql`${coachDetails.faithIntegration} && ARRAY[${sql.raw(faithValues)}]::text[]`,
      );
    }

    if (
      preferences.coachGender &&
      preferences.coachGender !== "No preference"
    ) {
      hardFilters.push(eq(coachDetails.gender, preferences.coachGender));
    }
  }

  // Extract user goals for expertise matching
  const userGoals = preferences.goals || [];
  if (preferences.otherGoal) {
    userGoals.push(preferences.otherGoal);
  }

  // Query coaches with vector similarity and join expertise data in a single query
  let coachesWithSimilarity = [];

  const ce = aliasedTable(coachEmbeddings, "ce");
  const cexp = aliasedTable(coachExpertise, "cexp");

  const hasNoPreferenceAge = preferences.coachAges.includes("No preference");
  const actualAgeRanges = preferences.coachAges.filter(
    (r) => r !== "No preference",
  );

  const vs = db.$with("vs").as(
    db
      .select({
        vectorSimilarity: preferences.embedding
          ? sql<number>`1 - (array[${sql.raw(preferences.embedding.join(","))}]::vector(1536) <=> ${ce.embedding})`.as(
              "vectorSimilarity",
            )
          : sql<number>`0`.as("vectorSimilarity"),
        coachId: ce.coachId,
        type: ce.type,
      })
      .from(ce)
      .where(eq(ce.type, CoachEmbeddingType.PreferencesMatch))
      .groupBy(ce.coachId, ce.embedding, ce.type),
  );

  const sources = db.$with("sources").as(
    db
      .select({
        coachId: coaches.id,

        primaryCategoryMatches:
          sql<number>`CAST(COUNT(CASE WHEN ${inArray(expertiseCategories.name, userGoals)} AND ${eq(cexp.type, ExpertiseType.Primary)} THEN 1 END) AS INTEGER)`.as(
            "primaryCategoryMatches",
          ),
        // Count rows within the group where the expertise type is Primary
        nPrimaryCategories:
          sql<number>`CAST(COUNT(CASE WHEN ${eq(cexp.type, ExpertiseType.Primary)} THEN 1 END) AS INTEGER)`.as(
            "nPrimaryCategories",
          ),
        // Count rows within the group where the category name is in userGoals AND the expertise type is Secondary
        secondaryCategoryMatches:
          sql<number>`CAST(COUNT(CASE WHEN ${inArray(expertiseCategories.name, userGoals)} AND ${eq(cexp.type, ExpertiseType.Secondary)} THEN 1 END) AS INTEGER)`.as(
            "secondaryCategoryMatches",
          ),
        // Count rows within the group where the expertise type is Secondary
        nSecondaryCategories:
          sql<number>`CAST(COUNT(CASE WHEN ${eq(cexp.type, ExpertiseType.Secondary)} THEN 1 END) AS INTEGER)`.as(
            "nSecondaryCategories",
          ),
      })
      .from(coaches)
      .innerJoin(cexp, eq(coaches.id, cexp.coachId))
      .innerJoin(
        expertiseSubcategories,
        eq(cexp.subcategoryId, expertiseSubcategories.id),
      )
      .innerJoin(
        expertiseCategories,
        eq(expertiseSubcategories.categoryId, expertiseCategories.id),
      )
      .groupBy(coaches.id),
  );

  // CTE to calculate coach age cleanly
  const coachAges = db.$with("coach_ages").as(
    db
      .select({
        coachId: coachDetails.coachId,
        // Calculate age only if dateOfBirth is not null
        age: sql<number>`CASE WHEN ${coachDetails.dateOfBirth} IS NOT NULL THEN EXTRACT(YEAR FROM AGE(NOW(), ${coachDetails.dateOfBirth})) ELSE NULL END`.as(
          "age",
        ),
      })
      .from(coachDetails),
  );

  const scores = db.$with("scores").as(
    db
      .select({
        coachId: sources.coachId,

        expertiseMatchScore:
          preferences.goals.length === 0 || preferences.goals.includes("Other")
            ? sql<number>`1.0`.as("expertiseMatchScore")
            : sql<number>`(
               0.75 * (CAST(${sources.primaryCategoryMatches} AS FLOAT) / GREATEST(${sources.nPrimaryCategories}, 1))
               + 0.25 * (CAST(${sources.secondaryCategoryMatches} AS FLOAT) / GREATEST(${sources.nSecondaryCategories}, 1))
            )`.as("expertiseMatchScore"),

        faithMatchScore:
          preferences.faithIntegration.length === 0 ||
          preferences.faithIntegration.includes("No preference")
            ? sql<number>`1.0`.as("faithMatchScore")
            : sql<number>`CASE 
                WHEN array_length(${coachDetails.faithIntegration}, 1) = 0
                THEN CAST(0.0 AS FLOAT)
                ELSE GREATEST(0.1, (
                  WITH faith_matches AS (
                    SELECT COUNT(*) AS match_count
                    FROM (
                      SELECT UNNEST(${coachDetails.faithIntegration}) AS faith
                      INTERSECT
                      SELECT UNNEST(${sql`ARRAY[${sql.join(preferences.faithIntegration)}]::text[]`}) AS faith
                    ) AS matches
                  )
                  SELECT CAST(faith_matches.match_count AS FLOAT) / CAST(${preferences.faithIntegration.length} AS FLOAT)
                  FROM faith_matches
                ))
              END`.as("faithMatchScore"),

        coachingApproachScore:
          preferences.coachingApproaches.length === 0 ||
          preferences.coachingApproaches.includes("Not sure yet")
            ? sql<number>`1.0`.as("coachingApproachScore")
            : sql<number>`CASE 
                WHEN array_length(${coachDetails.coachingApproaches}, 1) = 0
                THEN CAST(0.0 AS FLOAT)
                ELSE GREATEST(0.1, (
                  WITH approach_matches AS (
                    SELECT COUNT(*) AS match_count
                    FROM (
                      SELECT UNNEST(${coachDetails.coachingApproaches}) AS approach
                      INTERSECT
                      SELECT UNNEST(${sql`ARRAY[${sql.join(preferences.coachingApproaches)}]::text[]`}) AS approach
                    ) AS matches
                  )
                  SELECT CAST(approach_matches.match_count AS FLOAT) / CAST(${preferences.coachingApproaches.length} AS FLOAT)
                  FROM approach_matches
                ))
              END`.as("coachingApproachScore"),

        personalityMatchScore:
          preferences.coachPersonality.length === 0 ||
          preferences.coachPersonality.includes("Skip this question")
            ? sql<number>`1.0`.as("personalityMatchScore")
            : sql<number>`(
              ${generatePersonalityMatchSQL(preferences.coachPersonality as CoachPersonalityOptions[])}
            )::float /
            GREATEST(1, (
              (CASE WHEN ${isNotNull(personalityCategoryColumn("disc"))} THEN 1 ELSE 0 END) +
              (CASE WHEN ${isNotNull(personalityCategoryColumn("mbti"))} THEN 1 ELSE 0 END) +
              (CASE WHEN ${isNotNull(personalityCategoryColumn("enneagram"))} THEN 1 ELSE 0 END) +
              (CASE WHEN ${isNotNull(personalityCategoryColumn("humanDesign"))} THEN 1 ELSE 0 END)
            ))::float`.as("personalityMatchScore"),

        locationMatchScore: sql<number>`
          CASE
            WHEN ${
              !preferences.prefersCoachLocation ||
              preferences.prefersCoachLocation === "No preference"
            } THEN 1.0

            WHEN ${eq(profiles.primaryCity, preferences.coachLocationRegion ?? "")} THEN 1.0
            WHEN ${eq(profiles.closestMajorCity, preferences.coachLocationRegion ?? "")} THEN 0.8
            WHEN ${eq(profiles.primaryCountry, preferences.coachLocationCountry ?? "")} THEN 0.4

            ELSE 0.0
          END
        `.as("locationMatchScore"),

        genderScore:
          preferences.coachGender && preferences.coachGender !== "No preference"
            ? sql<number>`CASE WHEN ${coachDetails.gender} = ${preferences.coachGender} THEN CAST(1.0 AS FLOAT) ELSE CASE WHEN ${coachDetails.gender} IS NOT NULL THEN CAST(0.2 AS FLOAT) ELSE CAST(0.0 AS FLOAT) END END`.as(
                "genderScore",
              )
            : sql<number>`1.0`.as("genderScore"),

        experienceLevelScore:
          preferences.coachingPackages.length > 0 &&
          !preferences.coachingPackages.some(
            (p) => p.toLowerCase() === "no preference",
          )
            ? sql<number>`CASE WHEN ${inArray(coachDetails.experienceLevel, preferences.coachingPackages)} THEN CAST(1.0 AS FLOAT) ELSE CASE WHEN ${coachDetails.experienceLevel} IS NOT NULL THEN CAST(0.2 AS FLOAT) ELSE CAST(0.0 AS FLOAT) END END`.as(
                "experienceLevelScore",
              )
            : sql<number>`CAST(1.0 AS FLOAT)`.as("experienceLevelScore"),

        // --- New Age Match Score ---
        ageMatchScore: sql<number>`
          CAST(
            CASE
              WHEN ${hasNoPreferenceAge} THEN 1.0 -- Always 1.0 if user has no preference
              WHEN ${coachAges.age} IS NULL THEN 0.0        -- 0.0 if coach age is unknown
              ELSE
                CASE
                  -- Check if age falls into ANY selected range
                  WHEN (${actualAgeRanges.includes("20-30")} AND ${coachAges.age} >= 20 AND ${coachAges.age} < 30)
                    OR (${actualAgeRanges.includes("30-40")} AND ${coachAges.age} >= 30 AND ${coachAges.age} < 40)
                    OR (${actualAgeRanges.includes("40-50")} AND ${coachAges.age} >= 40 AND ${coachAges.age} < 50)
                    OR (${actualAgeRanges.includes("50+")}   AND ${coachAges.age} >= 50)
                  THEN 1.0 -- Match found
                  ELSE 0.0 -- No match
                END
            END
          AS FLOAT) -- Ensure the final result is float
        `.as("ageMatchScore"),
        // --- End New Age Match Score ---
      })
      .from(sources)
      .innerJoin(coachDetails, eq(sources.coachId, coachDetails.coachId))
      .innerJoin(profiles, eq(sources.coachId, profiles.coachId))
      // LEFT JOIN to bring in the calculated age from the coachAges CTE
      .leftJoin(coachAges, eq(sources.coachId, coachAges.coachId)),
  );

  const coachesData = await db
    // Include the new coachAges CTE
    .with(vs, coachAges, sources, scores)
    .select({
      coach: coaches,
      profile: profiles,
      details: coachDetails,
      expertiseSubcategories: sql<string>`(
        SELECT STRING_AGG(${expertiseSubcategories.name}, ', ') 
        FROM ${expertiseSubcategories}
        INNER JOIN ${coachExpertise} ON ${expertiseSubcategories.id} = ${coachExpertise.subcategoryId}
        WHERE ${coachExpertise.coachId} = ${coaches.id}
        AND ${coachExpertise.type} = ${ExpertiseType.Primary})`,

      vectorSimilarity: vs.vectorSimilarity,
      expertiseMatchScore: scores.expertiseMatchScore,
      faithMatchScore: scores.faithMatchScore,
      coachingApproachScore: scores.coachingApproachScore,
      personalityMatchScore: scores.personalityMatchScore,
      genderScore: scores.genderScore,
      experienceLevelScore: scores.experienceLevelScore,
      ageMatchScore: scores.ageMatchScore,
      locationMatchScore: scores.locationMatchScore,
    })
    .from(coaches)
    .innerJoin(profiles, eq(coaches.id, profiles.coachId))
    .innerJoin(coachDetails, eq(coaches.id, coachDetails.coachId))
    .innerJoin(
      vs,
      and(
        eq(coaches.id, vs.coachId),
        eq(vs.type, CoachEmbeddingType.PreferencesMatch),
      ),
    )
    .innerJoin(scores, eq(coaches.id, scores.coachId))
    .where(and(...hardFilters))
    .limit(queryLimit)
    .orderBy(
      desc(
        sql<number>`
        CAST(${matchingOptions.specialtyWeight} AS FLOAT) * ${scores.expertiseMatchScore}
        + CAST(${matchingOptions.faithWeight} AS FLOAT) * ${scores.faithMatchScore}
        + CAST(${matchingOptions.coachingApproachWeight} AS FLOAT) * ${scores.coachingApproachScore}
        + CAST(${matchingOptions.personalityWeight} AS FLOAT) * ${scores.personalityMatchScore}
        + CAST(${matchingOptions.genderWeight} AS FLOAT) * ${scores.genderScore}
        + CAST(${matchingOptions.vectorSimilarityWeight} AS FLOAT) * ${vs.vectorSimilarity}
        + CAST(${matchingOptions.experienceLevelWeight} AS FLOAT) * ${scores.experienceLevelScore}
        + CAST(${matchingOptions.ageWeight} AS FLOAT) * ${scores.ageMatchScore}
        + CAST(${matchingOptions.locationWeight} AS FLOAT) * ${scores.locationMatchScore}
      `,
      ),
    )
    .groupBy(
      coaches.id,
      profiles.id,
      coachDetails.id,
      vs.vectorSimilarity,
      scores.expertiseMatchScore,
      scores.faithMatchScore,
      scores.coachingApproachScore,
      scores.personalityMatchScore,
      scores.genderScore,
      scores.experienceLevelScore,
      scores.ageMatchScore,
      scores.locationMatchScore,
    );

  // Process the results to calculate specialty scores and other attribute scores
  coachesWithSimilarity = coachesData.map((coachData) => {
    return {
      coach: coachData.coach,
      profile: coachData.profile,
      details: coachData.details,
      vectorSimilarity: coachData.vectorSimilarity,
      expertise: coachData.expertiseSubcategories,
      specialtyScore: coachData.expertiseMatchScore,
      faithScore: coachData.faithMatchScore,
      coachingApproachScore: coachData.coachingApproachScore,
      personalityMatchScore: coachData.personalityMatchScore,
      genderScore: coachData.genderScore,
      experienceLevelScore: coachData.experienceLevelScore,
      ageScore: coachData.ageMatchScore,
      locationScore: Number(coachData.locationMatchScore),
    };
  });

  // Calculate attribute match score with improved point system
  const scoredCoaches = coachesWithSimilarity.map(
    (coach: {
      coach: typeof coaches.$inferSelect;
      profile: typeof profiles.$inferSelect;
      details: typeof coachDetails.$inferSelect;
      vectorSimilarity: number;
      expertise: string;
      specialtyScore: number;
      faithScore: number;
      coachingApproachScore: number;
      personalityMatchScore: number;
      genderScore: number;
      experienceLevelScore: number;
      locationScore: number;
      ageScore: number;
    }) => {
      const attributeScores = {
        specialty: coach.specialtyScore,
        location: coach.locationScore,
        faith: coach.faithScore,
        coachingApproach: coach.coachingApproachScore,
        personality: coach.personalityMatchScore,
        gender: coach.genderScore,
        age: coach.ageScore,
        sessionFormat: 0,
        experienceLevel: coach.experienceLevelScore,
      };

      // Session format matching (max 20 points)
      if (
        preferences.sessionDeliveryMethods &&
        Array.isArray(preferences.sessionDeliveryMethods) &&
        preferences.sessionDeliveryMethods.length > 0
      ) {
        // Check if coach's delivery methods include any user preferences
        const formatMatchScore = calculateFormatMatchScore(
          preferences.sessionDeliveryMethods,
          coach.details.sessionDeliveryMethods || [],
        );
        attributeScores.sessionFormat = formatMatchScore; // Scale to max 20 points

        // If coach didn't set delivery methods, give partial points
        if (
          !coach.details.sessionDeliveryMethods ||
          coach.details.sessionDeliveryMethods.length === 0
        ) {
          attributeScores.sessionFormat = 0.25;
        }
      } else {
        attributeScores.sessionFormat = 1; // Full points for no preference
      }

      // Calculate weighted attribute score
      const weightedAttributeScore =
        attributeScores.specialty * matchingOptions.specialtyWeight +
        attributeScores.location * matchingOptions.locationWeight +
        attributeScores.faith * matchingOptions.faithWeight +
        attributeScores.coachingApproach *
          matchingOptions.coachingApproachWeight +
        attributeScores.personality * matchingOptions.personalityWeight +
        attributeScores.gender * matchingOptions.genderWeight +
        attributeScores.age * matchingOptions.ageWeight +
        attributeScores.experienceLevel *
          matchingOptions.experienceLevelWeight +
        attributeScores.sessionFormat * matchingOptions.sessionFormatWeight +
        coach.vectorSimilarity * matchingOptions.vectorSimilarityWeight;

      // Normalize by sum of weights
      const weightSum =
        matchingOptions.specialtyWeight +
        matchingOptions.locationWeight +
        matchingOptions.faithWeight +
        matchingOptions.coachingApproachWeight +
        matchingOptions.personalityWeight +
        matchingOptions.genderWeight +
        matchingOptions.ageWeight +
        matchingOptions.experienceLevelWeight +
        matchingOptions.sessionFormatWeight +
        matchingOptions.vectorSimilarityWeight;

      const normalizedScore = weightedAttributeScore / weightSum;

      return {
        ...coach,
        similarity: normalizedScore,
        detailedScores: {
          vectorSimilarity: coach.vectorSimilarity,
          attributeComponents: attributeScores,
        },
      };
    },
  );

  // Sort by combined score and limit results
  return scoredCoaches
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, limit)
    .map((coach) => {
      console.log({
        coachName: coach.coach.fullName,
        dateOfBirth: coach.details.dateOfBirth,
        experienceLevel: coach.details.experienceLevel,
        coachingApproaches: coach.details.coachingApproaches,
        faith: coach.details.faithIntegration,
        discType: coach.details.discType,
        mbtiType: coach.details.mbtiType,
        enneagramType: coach.details.enneagramType,
        humanDesignType: coach.details.humanDesignType,
        similarity: coach.similarity,
        detailedScores: coach.detailedScores,
      });

      return coach;
    });
}, {});
